<template>
  <div style="padding: 24px">
    <a-card :bordered="false" title="用户管理">
      <!-- 搜索栏 -->
      <div style="margin-bottom: 16px; display: flex; gap: 8px">
        <a-input v-model:value="keyword" placeholder="搜索用户名/昵称" style="width: 220px" allow-clear
          @pressEnter="fetchList" />
        <a-button type="primary" @click="fetchList">搜索</a-button>
        <a-button @click="openCreate">新增用户</a-button>
      </div>

      <!-- 表格 -->
      <a-table :columns="columns" :data-source="list" :loading="loading" :pagination="pagination" row-key="id"
        @change="handleTableChange">
        <template #bodyCell="{ column, record }">
          <template v-if="column.dataIndex === 'isEnabled'">
            <a-tag :color="record.isEnabled ? 'green' : 'red'">
              {{ record.isEnabled ? '启用' : '禁用' }}
            </a-tag>
          </template>
          <template v-if="column.dataIndex === 'action'">
            <a-space>
              <a-button type="link" size="small" @click="openEdit(record)">编辑</a-button>
              <a-button type="link" size="small" @click="openResetPwd(record)">重置密码</a-button>
            </a-space>
          </template>
        </template>
      </a-table>
    </a-card>

    <!-- 新增/编辑弹窗 -->
    <a-modal v-model:open="modalVisible" :title="editingUser ? '编辑用户' : '新增用户'" :confirm-loading="saving"
      @ok="handleSave">
      <a-form :label-col="{ span: 5 }">
        <a-form-item label="用户名">
          <a-input v-model:value="form.username" :disabled="!!editingUser" placeholder="登录账号" />
        </a-form-item>
        <a-form-item v-if="!editingUser" label="密码">
          <a-input-password v-model:value="form.password" placeholder="初始密码" />
        </a-form-item>
        <a-form-item label="昵称">
          <a-input v-model:value="form.nickname" placeholder="显示名称" />
        </a-form-item>
        <a-form-item label="角色">
          <a-select v-model:value="form.roleId" :options="roleOptions" placeholder="选择角色" />
        </a-form-item>
        <a-form-item label="状态">
          <a-switch v-model:checked="form.isEnabled" checked-children="启用" un-checked-children="禁用" />
        </a-form-item>
      </a-form>
    </a-modal>

    <!-- 重置密码弹窗 -->
    <a-modal v-model:open="resetPwdVisible" title="重置密码" :confirm-loading="resettingPwd" @ok="handleResetPwd">
      <p>为用户 <b>{{ resetTarget?.username }}</b> 设置新密码：</p>
      <a-input-password v-model:value="newPassword" placeholder="新密码" />
    </a-modal>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from "vue";
import { message } from "ant-design-vue";
import { get, postJson } from "@/utils/request";
import { authStore } from "@/store/auth";
import router from "@/router";

/* ---------- 列表 ---------- */
const keyword = ref("");
const loading = ref(false);
const list = ref([]);
const pagination = reactive({ current: 1, pageSize: 20, total: 0 });

const columns = [
  { title: "ID", dataIndex: "id", width: 60 },
  { title: "用户名", dataIndex: "username" },
  { title: "昵称", dataIndex: "nickname" },
  { title: "角色", dataIndex: "roleName" },
  { title: "状态", dataIndex: "isEnabled", width: 80 },
  { title: "创建时间", dataIndex: "createdAt", width: 180 },
  { title: "操作", dataIndex: "action", width: 160 },
];

const fetchList = async () => {
  loading.value = true;
  try {
    const json = await get("/v1/analytics/system/user/list", {
      pageIdx: pagination.current,
      pageSize: pagination.pageSize,
      keyword: keyword.value || undefined,
    });
    if (json.code === 200) {
      list.value = json.data?.data || [];
      pagination.total = json.data?.total || 0;
    }
  } finally {
    loading.value = false;
  }
};

const handleTableChange = (pag) => {
  pagination.current = pag.current;
  pagination.pageSize = pag.pageSize;
  fetchList();
};

/* ---------- 角色选项 ---------- */
const roleOptions = ref([]);
const fetchRoles = async () => {
  try {
    const json = await get("/v1/analytics/system/role/list");
    if (json.code === 200 && Array.isArray(json.data)) {
      roleOptions.value = json.data.map((r) => ({
        label: r.name,
        value: r.id,
      }));
    }
  } catch {
    /* ignore */
  }
};

/* ---------- 新增/编辑 ---------- */
const modalVisible = ref(false);
const saving = ref(false);
const editingUser = ref(null);
const form = reactive({
  username: "",
  password: "",
  nickname: "",
  roleId: undefined,
  isEnabled: true,
});

const resetForm = () => {
  form.username = "";
  form.password = "";
  form.nickname = "";
  form.roleId = undefined;
  form.isEnabled = true;
};

const openCreate = () => {
  editingUser.value = null;
  resetForm();
  modalVisible.value = true;
};

const openEdit = (record) => {
  editingUser.value = record;
  form.username = record.username;
  form.nickname = record.nickname;
  form.roleId = record.roleId;
  form.isEnabled = record.isEnabled;
  modalVisible.value = true;
};

const handleSave = async () => {
  if (!form.username || !form.nickname || !form.roleId) {
    message.warning("请填写完整信息");
    return;
  }
  saving.value = true;
  try {
    const url = editingUser.value
      ? "/v1/analytics/system/user/update"
      : "/v1/analytics/system/user/create";
    const params = editingUser.value
      ? {
        id: editingUser.value.id,
        nickname: form.nickname,
        roleId: form.roleId,
        isEnabled: form.isEnabled,
      }
      : {
        username: form.username,
        password: form.password,
        nickname: form.nickname,
        roleId: form.roleId,
        isEnabled: form.isEnabled,
      };
    const json = await postJson(url, params);
    if (json.code === 200) {
      message.success(editingUser.value ? "更新成功" : "创建成功");
      modalVisible.value = false;
      fetchList();
    } else {
      message.error(json.content || "操作失败");
    }
  } finally {
    saving.value = false;
  }
};

/* ---------- 重置密码 ---------- */
const resetPwdVisible = ref(false);
const resettingPwd = ref(false);
const resetTarget = ref(null);
const newPassword = ref("");

const openResetPwd = (record) => {
  resetTarget.value = record;
  newPassword.value = "";
  resetPwdVisible.value = true;
};

const handleResetPwd = async () => {
  if (!newPassword.value) {
    message.warning("请输入新密码");
    return;
  }
  resettingPwd.value = true;
  try {
    const json = await postJson("/v1/analytics/system/user/reset-password", {
      id: resetTarget.value.id,
      newPassword: newPassword.value,
    });
    if (json.code === 200) {
      message.success("密码已重置");
      resetPwdVisible.value = false;
      // 若重置的是当前登录用户，清除认证并跳转重新登录
      if (resetTarget.value?.id === authStore.state.user?.id) {
        authStore.clearAuth();
        message.warning("您的密码已被修改，请重新登录");
        router.push("/login");
      }
    } else {
      message.error(json.content || "重置失败");
    }
  } finally {
    resettingPwd.value = false;
  }
};

/* ---------- 初始化 ---------- */
onMounted(() => {
  fetchList();
  fetchRoles();
});
</script>
